import { HomeSectionsButtons } from '@/app/pages/home/home-sections-buttons';
import InstagramLogo from '@/assets/img/instagram_icon.svg?react';
import { Button } from '@/components/ui/button';
import { useData } from '@/context/data.provider';
import { type AllowedLang, useUserInfo } from '@/context/user-info';
import { useResponsive } from '@/lib/useResponsive';
import { apple, googlePlay } from '@/misc/images';
import { resetLocalCookiesConsent } from '@/services/ga-tracking';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  frenchInfo,
  handleClickDownloadAndroid,
  handleClickDownloadApple,
  handleClickEspInsta,
  handleClickFraInsta,
  YegoOptions,
} from './utils';

export const Footer = () => {
  const { t } = useTranslation();
  const { userLanguage } = useUserInfo();
  const { cities } = useData();
  const { isMobile, sm, md } = useResponsive();
  const location = useLocation();

  const halfScreen = isMobile || sm || md;

  const [linkIsHovered, setLinkIsHovered] = useState<AllowedLang | null>(null);

  const isFrench = useMemo(() => userLanguage === 'fr', [userLanguage]);
  const isHomeScreen = useMemo(
    () => location.pathname === `/${userLanguage}`,
    [location.pathname, userLanguage],
  );

  return (
    <footer
      className="z-10 bg-bg-dark flex flex-col gap-24 text-white-light text-base font-medium pb-4 pt-6 md:pt-12"
      aria-label={t('translation:accessibility.aria_label.footer')}
    >
      <div className="flex flex-col md:flex-row items-center justify-evenly md:items-start gap-8 md:gap-4">
        {halfScreen && isHomeScreen && <HomeSectionsButtons halfScreen={halfScreen} />}
        <div className="flex flex-col items-center md:items-start gap-2.5">
          <div className="font-phosphate text-white text-xl">
            {t('translation:footer.follow_us.title').toUpperCase()}
          </div>
          <div className="flex flex-col gap-1">
            <Button
              variant="ghost"
              className="p-0 flex gap-2 text-base font-medium cursor-pointer capitalize hover:text-fg-secondary"
              id="social-media"
              onClick={handleClickEspInsta}
              onMouseEnter={() => setLinkIsHovered('es')}
              onMouseLeave={() => setLinkIsHovered(null)}
            >
              <InstagramLogo
                className="size-4"
                fill={linkIsHovered === 'es' ? '#fab415' : '#cadaec'}
              />
              <p className="text-white">{t('translation:footer.follow_us.link_es')}</p>
            </Button>
            <Button
              variant="ghost"
              className="p-0 flex gap-2 text-base font-medium cursor-pointer capitalize hover:text-fg-secondary"
              id="social-media"
              onClick={handleClickFraInsta}
              onMouseEnter={() => setLinkIsHovered('fr')}
              onMouseLeave={() => setLinkIsHovered(null)}
            >
              <InstagramLogo
                className="size-4"
                fill={linkIsHovered === 'fr' ? '#fab415' : '#cadaec'}
              />
              <p className="text-white">{t('translation:footer.follow_us.link_fr')}</p>
            </Button>
          </div>
        </div>
        <div className="flex flex-col items-center md:items-start gap-2.5">
          <div className="font-phosphate text-white text-xl">
            {t('translation:footer.yego.title')}
          </div>
          <div className="flex flex-col items-center md:items-start gap-1">
            {YegoOptions.map((option) => (
              <div
                key={option.id}
                className="flex items-center h-7 text-base cursor-pointer first-letter:capitalize"
              >
                {option.element === 'link' && (
                  <Link
                    className="text-white hover:text-fg-secondary"
                    to={`/${userLanguage}${option.to}`}
                  >
                    {t(option.label)}
                  </Link>
                )}
                {option.element === 'button' && (
                  <Button
                    variant="ghost"
                    onClick={() => option.onClick(userLanguage)}
                    className="text-base p-0 text-white hover:text-fg-secondary"
                  >
                    {t(option.label)}
                  </Button>
                )}
                {option.element === 'anchor' && (
                  <a
                    href={option.href}
                    className="text-white hover:text-fg-secondary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t(option.label)}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
        {isFrench && (
          <div className="flex flex-col items-center md:items-start gap-2.5">
            <div className="font-phosphate text-white text-xl">ROULEZ VERT</div>
            <div className="flex flex-col items-center md:items-start gap-1">
              {frenchInfo.map((info) => (
                <div
                  key={info.id}
                  className="flex items-center h-7 text-base cursor-pointer first-letter:capitalize"
                >
                  <a
                    href={info.href}
                    className="text-white hover:text-fg-secondary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {info.label}
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-col items-center md:items-start gap-2.5">
          <div className="font-phosphate text-white text-xl">
            {t('translation:footer.cities.title').toUpperCase()}
          </div>
          <div className="flex flex-col items-center md:items-start gap-1">
            {cities?.map((city) => (
              <div
                className="flex items-center h-7 cursor-pointer capitalize text-base"
                key={city.id}
              >
                <Link
                  className=" text-white hover:text-fg-secondary"
                  to={`/${userLanguage}/how-it-works/${city.name}`}
                >
                  {city.name}
                </Link>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-center md:items-start gap-2.5">
          <div className="font-phosphate text-white text-xl">
            {t('translation:footer.download_app.title').toUpperCase()}
          </div>

          <div className="flex flex-col md:flex-row items-center gap-4">
            <a
              className="cursor-pointer"
              onClick={handleClickDownloadApple}
              href="https://itunes.apple.com/es/app/yugo-mobility/id1181020675?mt=8"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('translation:accessibility.button.download_app_store')}
            >
              <img
                src={apple}
                className="w-40 h-auto transition-all duration-200 ease-in-out hover:scale-105 hover:brightness-125"
                alt={t('translation:accessibility.logo.alt.app_store')}
              />
            </a>
            <a
              onClick={handleClickDownloadAndroid}
              href="https://play.google.com/store/apps/details?id=com.getyugo.app"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('translation:accessibility.button.download_google_play')}
            >
              <img
                src={googlePlay}
                className="w-40 h-auto transition-all duration-200 ease-in-out hover:scale-105 hover:brightness-125"
                alt={t('translation:accessibility.logo.alt.google_play')}
                id="googleplay"
              />
            </a>
          </div>
          <div className="text-fg-secondary text-base">
            <span>{t('translation:footer.made_in_bcn')}</span>{' '}
            <span role="img" aria-hidden="true">
              💚
            </span>
          </div>
        </div>
      </div>
      <div className="w-full flex flex-col items-center text-center text-white gap-2 text-[13px] pt-4 px-4">
        <button type="button" onClick={resetLocalCookiesConsent}>
          {t('translation:footer.config_cookies.button')}
        </button>
        <p>{t('translation:footer.copyright', { date: new Date().getFullYear() })}</p>
      </div>
    </footer>
  );
};
